import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Loader2 } from "lucide-react";
import { initGA, trackPageView } from "./lib/analytics";
import { trackPageView as trackMetaPageView } from "./lib/metaPixel";

// Pages
import HomePage from "./pages/Home/HomePage";
import AuthCallback from "./pages/Auth/AuthCallback";
import LoginPage from "./pages/Auth/LoginPage";
import AdminLoginPage from "./pages/Auth/AdminLoginPage";
import AgenciesPage from "./pages/Agencies/AgenciesPage";
import AgencyDetailPage from "./pages/Agencies/AgencyDetailPage";
import PricingPage from "./pages/Pricing/PricingPage";
import DashboardPage from "./pages/Dashboard/DashboardPage";
import MultiApplyPage from "./pages/MultiApply/MultiApplyPage";
import ConsultationPage from "./pages/Consultation/ConsultationPage";
import PaymentSuccessPage from "./pages/Payment/PaymentSuccessPage";
import CheckoutPage from "./pages/Payment/CheckoutPage";
import ThankYouPage from "./pages/Payment/ThankYouPage";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import SuperAdminDashboard from "./pages/Admin/SuperAdminDashboard";
import AddJobPage from "./pages/Admin/AddJobPage";
import BlogPage from "./pages/Blog/BlogPage";
import PracticalCoursesPage from "./pages/PracticalCourses/PracticalCoursesPage";
import PracticalCourseDetailPage from "./pages/PracticalCourses/PracticalCourseDetailPage";
import AdminPracticalCoursesPage from "./pages/Admin/AdminPracticalCoursesPage";
import StaffDashboard from "./pages/Staff/StaffDashboard";
import BlogPostPage from "./pages/Blog/BlogPostPage";
import JobsPage from "./pages/Jobs/JobsPage";
import TermsPage from "./pages/Legal/TermsPage";
import PrivacyPage from "./pages/Legal/PrivacyPage";
import ModernSlaveryPage from "./pages/Legal/ModernSlaveryPage";
import SettingsPage from "./pages/Settings/SettingsPage";
import CVReviewPage from "./pages/CVReview/CVReviewPage";
import CVReviewSharePage from "./pages/CVReview/CVReviewSharePage";
import CookieConsent from "./components/layout/CookieConsent";
import Chatbot from "./components/layout/Chatbot";
import { MultiApplyUpsell } from "./components/layout/MultiApplyUpsell";

// Initialize Google Analytics
initGA();

// Page View Tracker Component (tracks both GA and Meta Pixel)
const PageViewTracker = () => {
    const location = useLocation();
    
    useEffect(() => {
        // Track page view in Google Analytics
        trackPageView(location.pathname + location.search);
        // Track page view in Meta Pixel
        trackMetaPageView();
    }, [location]);
    
    return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false, requireSuperAdmin = false, requireStaff = false }) => {
    const { user, loading, isAdmin, isSuperAdmin, isStaff } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-primary-600 animate-spin" />
            </div>
        );
    }

    // If user data was passed via location state (from auth callback), allow access
    if (location.state?.user) {
        return children;
    }

    if (!user) {
        return <Navigate to="/" replace />;
    }

    if (requireSuperAdmin && !isSuperAdmin) {
        return <Navigate to="/" replace />;
    }

    if (requireAdmin && !isAdmin) {
        return <Navigate to="/" replace />;
    }

    if (requireStaff && !isStaff && !isAdmin) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// App Router - Handles session_id detection before ProtectedRoute
const AppRouter = () => {
    const location = useLocation();

    // Check URL fragment (not query params) for session_id - CRITICAL for auth flow
    // This must happen synchronously during render, not in useEffect
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin-login" element={<AdminLoginPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/agencies" element={<AgenciesPage />} />
            <Route path="/agencies/:agencyId" element={<AgencyDetailPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/multi-apply" element={<MultiApplyPage />} />
            <Route path="/consultation" element={<ConsultationPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/payment/thank-you" element={<ThankYouPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/practical-courses" element={<PracticalCoursesPage />} />
            <Route path="/practical-courses/:slug" element={<PracticalCourseDetailPage />} />
            <Route path="/admin/practical-courses" element={<AdminPracticalCoursesPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/modern-slavery-statement" element={<ModernSlaveryPage />} />
            <Route path="/cv-review" element={<CVReviewPage />} />
            <Route path="/cv-review/share/:shareToken" element={<CVReviewSharePage />} />

            {/* Protected Routes */}
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/settings" 
                element={
                    <ProtectedRoute>
                        <SettingsPage />
                    </ProtectedRoute>
                } 
            />

            {/* Admin Routes */}
            <Route 
                path="/admin" 
                element={
                    <ProtectedRoute requireAdmin>
                        <AdminDashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/jobs/new" 
                element={
                    <ProtectedRoute requireAdmin>
                        <AddJobPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/jobs/:jobId/edit" 
                element={
                    <ProtectedRoute requireAdmin>
                        <AddJobPage />
                    </ProtectedRoute>
                } 
            />

            {/* Super Admin Routes */}
            <Route 
                path="/super-admin" 
                element={
                    <ProtectedRoute requireSuperAdmin>
                        <SuperAdminDashboard />
                    </ProtectedRoute>
                } 
            />

            {/* Staff Routes */}
            <Route
                path="/staff"
                element={
                    <ProtectedRoute requireStaff>
                        <StaffDashboard />
                    </ProtectedRoute>
                }
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <AppRouter />
                    <CookieConsent />
                    <Chatbot />
                    <MultiApplyUpsell />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
